import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  CustomDropdown,
  CustomDatePicker,
  todayCalendarDate,
  codePrimaryTerm,
  codesFromConfigFetchResponse,
  buildCodeDropdownOptions,
} from '@medprompt/shared';
import { ConfigService, sharedApiClient } from '../../fetcher';
import {
  WorkflowConditionEnum,
  WORKFLOW_CONDITION_TYPE_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  createDefaultCondition,
  getWorkflowConditionParamKey,
} from '../WorkflowConditionEnum';
import { RISK_CATEGORY_OPTIONS } from '../WorkflowActionEnum';
import '../WorkflowView.css';

const WorkflowConditionCard = ({ condition, index, onChange, onRemove, activeUsers, teams }) => {
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [paramDropdownOpen, setParamDropdownOpen] = useState(false);

  const configManager = useMemo(() => new ConfigService(sharedApiClient), []);
  const [letterTypes, setLetterTypes] = useState([]);
  const [letterTypeFetchState, setLetterTypeFetchState] = useState('idle');

  useEffect(() => {
    if (condition.type !== WorkflowConditionEnum.LETTER_TYPE_EQUALS.value) {
      return;
    }

    let cancelled = false;
    setLetterTypeFetchState('loading');
    configManager
      .fetchLetterTypesForDocument()
      .then((data) => {
        if (cancelled) return;
        setLetterTypes(codesFromConfigFetchResponse(data));
        setLetterTypeFetchState('loaded');
      })
      .catch((err) => {
        console.error('Error loading letter types:', err);
        if (!cancelled) {
          setLetterTypes([]);
          setLetterTypeFetchState('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [condition.type, configManager]);

  const letterTypeOptions = useMemo(() => buildCodeDropdownOptions(letterTypes), [letterTypes]);

  const handleTypeChange = (option) => {
    onChange(createDefaultCondition(option.value));
    setTypeDropdownOpen(false);
  };

  const handleParamChange = (value) => {
    onChange({ ...condition, [getWorkflowConditionParamKey(condition.type)]: value });
    setParamDropdownOpen(false);
  };

  const renderParameterInput = () => {
    switch (condition.type) {
      case WorkflowConditionEnum.ASSIGNEE_EQUALS.value:
        const assigneeOptions = [
          { id: null, name: 'Select user or team...', display: 'Select user or team...' },
          ...(activeUsers || []).map(u => ({ id: u.userId, name: u.name, display: u.name, type: 'user' })),
          ...(teams || []).map(t => ({ id: t.teamId, name: t.teamName, display: t.teamName, type: 'team' }))
        ];
        const selectedAssignee = assigneeOptions.find(opt => opt.id === condition.assigneeId) || assigneeOptions[0];

        return (
          <CustomDropdown
            label="User/Team"
            selected={selectedAssignee.display}
            options={assigneeOptions}
            isOpen={paramDropdownOpen}
            toggleDropdown={() => setParamDropdownOpen(!paramDropdownOpen)}
            onSelect={(option) => handleParamChange(option.id)}
            keyExtractor={(option) => `assignee-${option.id}`}
          />
        );

      case WorkflowConditionEnum.RISK_CATEGORY_EQUALS.value:
        const selectedRisk = RISK_CATEGORY_OPTIONS.find(opt => opt.value === condition.riskCategory) || RISK_CATEGORY_OPTIONS[0];
        return (
          <CustomDropdown
            label="Urgency"
            selected={selectedRisk.name}
            options={RISK_CATEGORY_OPTIONS}
            isOpen={paramDropdownOpen}
            toggleDropdown={() => setParamDropdownOpen(!paramDropdownOpen)}
            onSelect={(option) => handleParamChange(option.value)}
            keyExtractor={(option) => `risk-${option.value}`}
          />
        );

      case WorkflowConditionEnum.LETTER_TYPE_EQUALS.value:
        if (letterTypeFetchState === 'loading') {
          return <em style={{ color: '#888' }}>Loading letter types…</em>;
        }
        if (letterTypeFetchState === 'error') {
          return <em style={{ color: '#888' }}>Could not load letter types</em>;
        }
        return (
          <CustomDropdown
            label="Letter Type"
            searchable
            selected={codePrimaryTerm(condition.letterTypeCode)}
            searchPlaceholder="Search letter types…"
            options={letterTypeOptions}
            isOpen={paramDropdownOpen}
            toggleDropdown={() => setParamDropdownOpen(!paramDropdownOpen)}
            onSelect={(option) => handleParamChange(option)}
            keyExtractor={(option) => `letter-${option.code}`}
            disabled={letterTypeFetchState !== 'loaded'}
          />
        );

      case WorkflowConditionEnum.IS_DAY_OF_WEEK.value:
        const selectedDay = DAY_OF_WEEK_OPTIONS.find(opt => opt.value === condition.dayOfWeek) || DAY_OF_WEEK_OPTIONS[0];
        return (
          <CustomDropdown
            label="Day of Week"
            selected={selectedDay.name}
            options={DAY_OF_WEEK_OPTIONS}
            isOpen={paramDropdownOpen}
            toggleDropdown={() => setParamDropdownOpen(!paramDropdownOpen)}
            onSelect={(option) => handleParamChange(option.value)}
            keyExtractor={(option) => `day-${option.value}`}
          />
        );

      case WorkflowConditionEnum.DATE_EQUALS.value:
        return (
          <CustomDatePicker
            label="Date"
            value={condition.date || todayCalendarDate()}
            onChange={(value) => onChange({ ...condition, date: value })}
          />
        );

      case WorkflowConditionEnum.SUMMARY_LIKE.value:
        return (
          <div className="comm-content-section">
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Query:
            </label>
            <textarea
              className="workflow-comment-textarea"
              value={condition.query || ''}
              onChange={(e) => onChange({ ...condition, query: e.target.value })}
              placeholder='e.g. "mentions cancer" or "requires urgent follow-up"'
              rows={3}
            />
          </div>
        );

      default:
        if (!WorkflowConditionEnum[condition.type]?.hasParameters) {
          return <div style={{ padding: '10px', color: '#666', fontStyle: 'italic' }}>No additional parameters required</div>;
        }
        return null;
    }
  };

  const selectedType = WORKFLOW_CONDITION_TYPE_OPTIONS.find(opt => opt.value === condition.type) || WORKFLOW_CONDITION_TYPE_OPTIONS[0];

  return (
    <div className="workflow-card">
      <div className="workflow-card-header">
        <CustomDropdown
          label="Condition Type"
          selected={selectedType.name}
          options={WORKFLOW_CONDITION_TYPE_OPTIONS}
          isOpen={typeDropdownOpen}
          toggleDropdown={() => setTypeDropdownOpen(!typeDropdownOpen)}
          onSelect={handleTypeChange}
          keyExtractor={(option) => `type-${option.value}`}
          width="300px"
        />
        <button
          className="workflow-card-remove"
          onClick={() => onRemove(index)}
          title="Remove condition"
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <div className="workflow-card-body">
        {renderParameterInput()}
      </div>
    </div>
  );
};

export default WorkflowConditionCard;
